// Centralized labels / options for every vehicle enum the UI touches.
// Kept in lock-step with packages/db/convex/inventory/validators.ts.

export type VehicleStatus =
  | "draft"
  | "in_recon"
  | "available"
  | "hold"
  | "sale_pending"
  | "sold"
  | "delivered"
  | "unwound"
  | "archived"

export type InventoryType = "new" | "used" | "certified_pre_owned" | "wholesale"

export type VehicleBodyType = "car" | "truck" | "suv" | "van" | "commercial" | "other"

export type FuelType = "gas" | "diesel" | "hybrid" | "plug_in_hybrid" | "electric" | "other"

export type Drivetrain = "fwd" | "rwd" | "awd" | "4wd" | "other"

export type Transmission = "automatic" | "manual" | "cvt" | "dct" | "other"

export type OdometerStatus = "actual" | "exceeds_limits" | "true_mileage_unknown" | "exempt"

export type TitleStatus =
  | "clean"
  | "salvage"
  | "restored"
  | "lemon"
  | "dismantled"
  | "bonded"
  | "other"

export const VEHICLE_STATUS_LABELS: Record<VehicleStatus, string> = {
  draft: "Draft",
  in_recon: "In recon",
  available: "Available",
  hold: "Hold",
  sale_pending: "Sale pending",
  sold: "Sold",
  delivered: "Delivered",
  unwound: "Unwound",
  archived: "Archived",
}

export const VEHICLE_STATUS_OPTIONS: VehicleStatus[] = [
  "draft",
  "in_recon",
  "available",
  "hold",
  "sale_pending",
  "sold",
  "delivered",
  "unwound",
  "archived",
]

export const INVENTORY_TYPE_LABELS: Record<InventoryType, string> = {
  new: "New",
  used: "Used",
  certified_pre_owned: "CPO",
  wholesale: "Wholesale",
}

export const INVENTORY_TYPE_OPTIONS: InventoryType[] = [
  "new",
  "used",
  "certified_pre_owned",
  "wholesale",
]

export const VEHICLE_BODY_LABELS: Record<VehicleBodyType, string> = {
  car: "Car",
  truck: "Truck",
  suv: "SUV",
  van: "Van",
  commercial: "Commercial",
  other: "Other",
}

export const VEHICLE_BODY_OPTIONS: VehicleBodyType[] = [
  "car",
  "truck",
  "suv",
  "van",
  "commercial",
  "other",
]

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  gas: "Gas",
  diesel: "Diesel",
  hybrid: "Hybrid",
  plug_in_hybrid: "Plug-in hybrid",
  electric: "Electric",
  other: "Other",
}

export const FUEL_TYPE_OPTIONS: FuelType[] = [
  "gas",
  "diesel",
  "hybrid",
  "plug_in_hybrid",
  "electric",
  "other",
]

export const DRIVETRAIN_LABELS: Record<Drivetrain, string> = {
  fwd: "FWD",
  rwd: "RWD",
  awd: "AWD",
  "4wd": "4WD",
  other: "Other",
}

export const DRIVETRAIN_OPTIONS: Drivetrain[] = ["fwd", "rwd", "awd", "4wd", "other"]

export const TRANSMISSION_LABELS: Record<Transmission, string> = {
  automatic: "Automatic",
  manual: "Manual",
  cvt: "CVT",
  dct: "DCT",
  other: "Other",
}

export const TRANSMISSION_OPTIONS: Transmission[] = ["automatic", "manual", "cvt", "dct", "other"]

export const ODOMETER_STATUS_LABELS: Record<OdometerStatus, string> = {
  actual: "Actual mileage",
  exceeds_limits: "Exceeds mechanical limits",
  true_mileage_unknown: "True mileage unknown",
  exempt: "Exempt",
}

export const ODOMETER_STATUS_OPTIONS: OdometerStatus[] = [
  "actual",
  "exceeds_limits",
  "true_mileage_unknown",
  "exempt",
]

export const TITLE_STATUS_LABELS: Record<TitleStatus, string> = {
  clean: "Clean",
  salvage: "Salvage",
  restored: "Restored",
  lemon: "Lemon",
  dismantled: "Dismantled",
  bonded: "Bonded",
  other: "Other",
}

export const TITLE_STATUS_OPTIONS: TitleStatus[] = [
  "clean",
  "salvage",
  "restored",
  "lemon",
  "dismantled",
  "bonded",
  "other",
]

// Status tone is carried by the dot color alone. Text stays in neutral
// foreground/muted tones so the list reads quietly across many rows.
export const VEHICLE_STATUS_TONE: Record<VehicleStatus, { dot: string; text: string }> = {
  draft: { dot: "bg-muted-foreground/40", text: "text-muted-foreground" },
  in_recon: { dot: "bg-amber-500", text: "text-foreground" },
  available: { dot: "bg-emerald-500", text: "text-foreground" },
  hold: { dot: "bg-sky-500", text: "text-foreground" },
  sale_pending: { dot: "bg-violet-500", text: "text-foreground" },
  sold: { dot: "bg-primary/70", text: "text-muted-foreground" },
  delivered: { dot: "bg-primary/70", text: "text-muted-foreground" },
  unwound: { dot: "bg-rose-500", text: "text-foreground" },
  archived: { dot: "bg-muted-foreground/30", text: "text-muted-foreground" },
}
